import { useCallback, useEffect, useState } from "react";

import { Controller, useFormContext, useWatch } from "react-hook-form";

import {
  Autocomplete,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { ProductCategoryTypeEnum } from "../../../product-categories";
import { OutletProductSelect } from "../../../products";
import { apiClient } from "../../../shared";
import { CustomInputField, CustomNumberField } from "../../../ui";
import { LensProductVariantSection } from "./lens-product-variant-section";

export const AddLensProductSection = () => {
  const { control, setValue } = useFormContext();

  const [buyer, seller, sellerableType] = useWatch({
    control,
    name: ["buyer", "seller", "sellerableType"],
  });

  // On an internal purchase the seller is a HUB outlet and the backend rejects
  // lines it can't access, so the catalogue has to be listed as the seller. An
  // external supplier has no product scope — fall back to the buyer.
  const productOutletId =
    sellerableType === "OUTLET" ? seller?.id : buyer?.id;

  const [product, setProduct] = useState(null);

  const [loadingVariantAttributeValue, setLoadingVariantAttributeValue] =
    useState(false);
  const [productVariantAttributeValue, setProductVariantAttributeValue] =
    useState(null);
  const [loadingProductAddOns, setLoadingProductAddOns] = useState(false);
  const [productAddOns, setProductAddOns] = useState([]);

  const fetchVariantAttributeValue = useCallback(async () => {
    setProductVariantAttributeValue(null);
    setLoadingVariantAttributeValue(true);
    try {
      const {
        data: { data },
      } = await apiClient.get(
        `products/${product.id}/product-variant-attribute-values`
      );
      console.log(data);
      setProductVariantAttributeValue(data);
    } finally {
      setLoadingVariantAttributeValue(false);
    }
  }, [product]);

  const fetchProductAddOn = useCallback(async () => {
    setProductAddOns([]);
    setLoadingProductAddOns(true);
    try {
      const {
        data: { data },
      } = await apiClient.get(`products/${product.id}/product-add-ons`);
      console.log(data);
      setProductAddOns(data);
    } finally {
      setLoadingProductAddOns(false);
    }
  }, [product]);

  useEffect(() => {
    if (product) {
      fetchVariantAttributeValue();
      fetchProductAddOn();
    } else {
      setProductAddOns([]);
      setProductVariantAttributeValue(null);
    }
  }, [fetchProductAddOn, fetchVariantAttributeValue, product]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 6 }}>
          <OutletProductSelect
            outletId={productOutletId}
            value={product}
            onChange={(value) => {
              setValue("productAddOn", null);
              setProduct(value);
            }}
            productCategoryType={ProductCategoryTypeEnum.LENS}
            TextFieldProps={{
              required: true,
              label: "Produk",
              // error: fieldState.invalid,
              // helperText: fieldState.error?.message,
            }}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <Controller
            control={control}
            name="productAddOn"
            render={({ field }) => (
              <Autocomplete
                value={field.value || null}
                onChange={(_, newValue) => field.onChange(newValue)}
                label="Add-On"
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.name}
                options={productAddOns}
                renderInput={(params) => (
                  <CustomInputField
                    {...params}
                    label="Add-On"
                    InputProps={{
                      ...params.slotProps.input,
                      placeholder: "Pilih",
                      endAdornment: (
                        <>
                          {loadingProductAddOns ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.slotProps.input.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <Controller
            control={control}
            name="subtotal"
            render={({ field, fieldState }) => (
              <CustomNumberField
                value={field.value} onChange={field.onChange}
                required
                label="Harga"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>
        {loadingVariantAttributeValue && (
          <Grid size={{ xs: 12 }}>
            <Typography
              sx={{
                color: "text.disabled",
                fontStyle: "italic"
              }}>
              Loading ...
            </Typography>
          </Grid>
        )}
        {product && productVariantAttributeValue && (
          <>
            <Grid size={{ xs: 12 }}>
              <Divider />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <LensProductVariantSection
                productId={product.id}
                fieldNamePrefix="leftSide"
                header="Sisi Kiri"
                productVariantAttributeValue={productVariantAttributeValue}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <LensProductVariantSection
                productId={product.id}
                fieldNamePrefix="rightSide"
                header="Sisi Kanan"
                productVariantAttributeValue={productVariantAttributeValue}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
};
